import { useTranslation } from 'react-i18next';
import type { Static } from '@sinclair/typebox';
import Text from '@ingka/text';
import { memo } from 'react';
import { Type } from '@sinclair/typebox';
import type { FormModuleProps, ModuleTransformer } from '../types';
import styles from '../../LotteryForm.module.scss';
import { ControlledEmailCheckboxes } from '../../components/controlled-email-checkboxes/ControlledEmailCheckboxes';
import { useFormModule } from '../../utils/use-form-module';

export const MODULE_NAME = 'email-subscription';

export type EmailSubscriptionFormType = Static<typeof EmailSubscriptionFormSchema>;

export const EmailSubscriptionFormSchema = Type.Object(
  {
    emailSubscription: Type.Object({
      emailMarketingEnabled: Type.Boolean(),
      emailMarketingRequired: Type.Boolean(),
    }),
  },
  {
    additionalProperties: false,
    $id: 'EmailSubscriptionSchema',
  }
);

export const EmailSubscriptionTransformer: ModuleTransformer<EmailSubscriptionFormType> = {
  fromPayload: ({ lottery }) => {
    return {
      emailSubscription: {
        emailMarketingEnabled:
          lottery?.familyAccount?.subscriptions.emailSubscription?.emailMarketingEnabled ?? false,
        emailMarketingRequired:
          lottery?.familyAccount?.subscriptions.emailSubscription?.emailMarketingRequired ?? false,
      },
    };
  },
  toPayload: (formData) => {
    return {
      lottery: {
        familyAccount: {
          subscriptions: {
            emailSubscription: {
              emailMarketingEnabled: formData.emailSubscription.emailMarketingEnabled ?? false,
              emailMarketingRequired: formData.emailSubscription.emailMarketingRequired ?? false,
            },
          },
        },
      },
    };
  },
};

const defaultValues: EmailSubscriptionFormType = {
  emailSubscription: {
    emailMarketingEnabled: false,
    emailMarketingRequired: false,
  },
};

export type EmailSubscriptionFormProps = FormModuleProps<EmailSubscriptionFormType>;

export const EmailSubscriptionForm: React.FC<EmailSubscriptionFormProps> = memo(
  ({ values, onChange, onValidation }) => {
    const { t } = useTranslation();
    const { control } = useFormModule({
      moduleName: MODULE_NAME,
      defaultValues: values || defaultValues,
      schema: EmailSubscriptionFormSchema,
      onChange,
      onValidation,
    });

    return (
      <form>
        <div className={styles['text-container']}>
          <Text headingSize="m">{t('form-email-checkbox-group-title')}</Text>
          <Text>{t('form-email-checkbox-group-description')}</Text>
          <ControlledEmailCheckboxes control={control} />
        </div>
      </form>
    );
  }
);

export const module = {
  name: MODULE_NAME,
  transformer: EmailSubscriptionTransformer,
  form: EmailSubscriptionForm,
} as const;
